import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { isAppRedirect, extRedirectOk } from '../lib/api';

/**
 * Handoff page for the Chrome extension's sign-in. The extension opens this in
 * a chrome.identity popup with ?ext_redirect=<chromiumapp redirect>. We:
 *   - if logged in → mint an extension token and bounce it back to the
 *     extension via the redirect URL fragment (#token=…);
 *   - if not → run the normal Google login first (returning here after).
 *
 * The token is only ever sent to a verified *.chromiumapp.org redirect, so a
 * crafted ext_redirect can't exfiltrate it.
 *
 * The phone apps use the same page with ?ext_redirect=<their own scheme> plus a
 * PKCE ?code_challenge= — riffloop://auth for shipped builds and the iPhone, and
 * <applicationId>://auth for newer Android builds, which need one target each so
 * that two builds on a phone can't take each other's redirect. Any app can claim a
 * custom scheme, so none of them gets the token: each gets a one-time code bound to
 * the challenge, traded with the verifier for the token at /api/auth/app-token.
 */
export function ExtensionAuth() {
  const [msg, setMsg] = useState('Connecting…');
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const extRedirect = params.get('ext_redirect') || '';
      const isApp = isAppRedirect(extRedirect);
      // S256 challenge = base64url(sha256(verifier)): always exactly 43 chars.
      const challenge = params.get('code_challenge') || '';
      // Only ever hand a credential to a Chrome extension's own redirect URL,
      // or a code to the Android app.
      if (!extRedirectOk(extRedirect) || (isApp && !/^[A-Za-z0-9_-]{43}$/.test(challenge))) {
        // No safe place to bounce anything — show an inline message instead.
        setErr(true);
        setMsg(isApp ? 'Open this from the Riffloop app to sign in.' : 'Open this from the Riffloop extension to sign in.');
        return;
      }

      // Always return to the extension's redirect URL — success OR failure.
      // launchWebAuthFlow only resolves (and closes the popup) on a nav to
      // chromiumapp.org, so any error MUST bounce there too, never park here.
      const back = (hash: string) => window.location.replace(extRedirect + hash);

      try {
        const me = await fetch('/api/user/me', { credentials: 'include' }).then((r) => r.json());
        if (!me) {
          const next = '/extension-auth?ext_redirect=' + encodeURIComponent(extRedirect) +
            (isApp ? '&code_challenge=' + encodeURIComponent(challenge) : '');
          if (isApp) {
            // The Android app asked for Google by name — its own button says so, and it has a
            // native email+password form of its own, so it never needs this chooser. Sending it
            // to /login made the user pick Google a second time on a page they did not ask for.
            // The extension keeps the chooser: its button is method-neutral, and jumping
            // straight to Google there would make Google the only way to connect it.
            window.location.href =
              '/api/auth/google/start?next=' + encodeURIComponent(next) +
              '&ext_redirect=' + encodeURIComponent(extRedirect);
            return;
          }
          // Not signed in yet → the normal login page, which offers Google AND
          // email+password, then return here. ?next= carries ext_redirect through, so
          // whichever method they pick lands back here to mint the token.
          window.location.href =
            '/login?next=' + encodeURIComponent(next) +
            '&ext_redirect=' + encodeURIComponent(extRedirect);
          return;
        }
        if (isApp) {
          // Android: a one-time code bound to the app's PKCE challenge, never
          // the token itself (see the header comment).
          const res = await fetch('/api/auth/app-code', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code_challenge: challenge }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data.code) {
            back('#error=token');
            return;
          }
          setMsg('Signed in — returning to the app…');
          back('#code=' + encodeURIComponent(data.code));
          return;
        }
        const res = await fetch('/api/auth/extension-token', { method: 'POST', credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.token) {
          back('#error=token');
          return;
        }
        setMsg('Signed in — returning to the extension…');
        back(
          '#token=' + encodeURIComponent(data.token) +
          '&user=' + encodeURIComponent(JSON.stringify(data.user || null)),
        );
      } catch {
        back('#error=network');
      }
    })();
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: 24,
        textAlign: 'center',
      }}
    >
      <Logo size="lg" variant="horizontal" />
      {!err && <div className="rl-spinner" />}
      <p style={{ color: 'var(--text-dim)', fontSize: 14, maxWidth: 340, lineHeight: 1.5 }}>{msg}</p>
    </div>
  );
}
